// Everyone in the church with their role, join date and teams. Same sources as
// the shared Members page: the church membership rows, user details from the
// auth service, and each active team's member list for team assignment.
import { useCallback, useEffect, useState } from 'react';
import { authApi } from '@app/api/auth';
import { groupsApi, membershipsApi } from '@app/api/organization';
import { ChurchMembershipState } from '@app/types';
import { logger } from '@app/utils/logger';

export interface ChurchPerson {
  membershipId: string;
  userId: string;
  name: string;
  email: string;
  role: string;
  function?: string;
  joinedAt: string;
  churchState: ChurchMembershipState;
  isActive: boolean;
  groupIds: string[];
  /** A team they asked to join but aren't on yet (e.g. it was full at approval). */
  pendingGroupId?: string;
}

const toState = (raw: unknown, isActive: boolean): ChurchMembershipState => {
  if (typeof raw === 'number') return raw as ChurchMembershipState;
  if (typeof raw === 'string') {
    const n = raw.toLowerCase();
    if (n === 'pending') return ChurchMembershipState.Pending;
    if (n === 'active') return ChurchMembershipState.Active;
  }
  return isActive ? ChurchMembershipState.Active : ChurchMembershipState.None;
};

export function useChurchPeople(churchId: string | null | undefined) {
  const [people, setPeople] = useState<ChurchPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async () => {
    if (!churchId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const [rows, groups] = await Promise.all([membershipsApi.getChurchMembers(churchId), groupsApi.getByChurch(churchId)]);
      const active = groups.filter((g) => g.isActive);
      const [users, teamLists] = await Promise.all([
        // Best-effort: names fall back to the membership row if the auth
        // service can't be reached, rather than failing the whole list.
        authApi.batchGetUsers(Array.from(new Set(rows.map((r) => r.userId)))).catch((err) => {
          logger.warn('User details unavailable; using membership names', err);
          return [];
        }),
        Promise.all(active.map(async (g) => ({ id: g.id, members: await membershipsApi.getGroupMembers(g.id).catch(() => []) }))),
      ]);
      const teamsByUser = new Map<string, string[]>();
      for (const t of teamLists) {
        for (const m of t.members) {
          if (m.isActive === false) continue;
          teamsByUser.set(m.userId, [...(teamsByUser.get(m.userId) ?? []), t.id]);
        }
      }
      const seen = new Set<string>();
      const list: ChurchPerson[] = [];
      for (const r of rows) {
        if (seen.has(r.userId)) continue;
        seen.add(r.userId);
        const u = users.find((x) => x.id === r.userId);
        const groupIds = new Set(teamsByUser.get(r.userId) ?? []);
        if (r.group?.groupId && active.some((g) => g.id === r.group!.groupId)) groupIds.add(r.group.groupId);
        list.push({
          membershipId: r.id,
          userId: r.userId,
          name: u?.username || r.username || 'Unknown user',
          email: u?.email || r.email || '',
          role: r.role,
          function: r.function,
          joinedAt: r.joinedAt,
          churchState: toState((r as { churchState?: unknown }).churchState, r.isActive),
          isActive: r.isActive,
          groupIds: Array.from(groupIds),
          pendingGroupId: groupIds.size === 0 ? r.pendingGroupId || undefined : undefined,
        });
      }
      setPeople(list.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (err) {
      logger.error('Failed to load church people', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [churchId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { people, setPeople, loading, error, reload: load };
}
